import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import {
  StatCard, ChartCard, LineChart, BarChart, DonutChart, ProductsTable, DrilldownModal, NeedsAttentionList,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  StatCardData, RevenueDataPoint, TopCustomer, BestSellingProduct, OrderStatusData, RoutePerformance,
} from './dashboardTypes';
import { getAdminDashboard, AdminDashboardData } from '../../../services/salesAnalyticsApi';
import styles from './AdminDashboard.module.css';

const TrendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>
);
const CustomersIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
);
const ProductIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
);
const OrderIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1z"/></svg>
);
const RouteIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
);

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000).toLocaleString()}K`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

const STATUS_COLOR: Record<string, string> = {
  delivered: '#10B981',
  paid: '#10B981',
  in_transit: '#3B82F6',
  shipped: '#3B82F6',
  pending: '#F59E0B',
  confirmed: '#F59E0B',
  processing: '#8B5CF6',
};

const CATEGORY_COLORS: Array<BestSellingProduct['categoryColor']> = ['blue', 'green', 'teal', 'purple', 'orange'];

/** KPI cards backed by this month's order data — clicking one opens the order-level drilldown. */
const DRILLDOWN_CARDS = new Set(['total-revenue', 'gross-margin', 'avg-order-value']);

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState<string | null>(null);

  const load = (withCompare: boolean) => {
    setLoading(true); setError(null);
    getAdminDashboard({ compare: withCompare })
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(compare); }, [compare]);

  const pctLabel = (cur: number, prev: number): string | undefined => {
    if (prev <= 0) return undefined;
    const change = Math.round(((cur - prev) / prev) * 100);
    return `${change >= 0 ? '+' : ''}${change}% vs last period`;
  };

  const stats: StatCardData[] = useMemo(() => {
    if (!data) return [];
    const s = data.stats;
    return [
      { id: 'total-revenue', title: 'Total Revenue', value: fmtMoney(s.total_revenue), change: s.revenue_change_pct, icon: 'revenue', color: 'blue' },
      { id: 'gross-margin', title: 'Gross Margin %', value: `${s.gross_margin_pct.toFixed(1)}%`, change: 0, icon: 'margin', color: 'teal' },
      { id: 'active-customers', title: 'Active Customers', value: String(s.active_customers), change: s.customers_change_pct, icon: 'customers', color: 'orange' },
      { id: 'orders-today', title: 'Orders Today', value: String(s.orders_today), change: s.orders_today_change_pct, icon: 'orders', color: 'red' },
      { id: 'avg-order-value', title: 'Avg Order Value', value: fmtMoney(s.avg_order_value), change: s.aov_change_pct, icon: 'avgOrder', color: 'green' },
      { id: 'pending-orders', title: 'Pending Orders', value: String(s.pending_orders), change: 0, icon: 'acquisition', color: 'pink' },
      { id: 'open-tickets', title: 'Open Tickets', value: String(s.open_tickets), change: 0, icon: 'ratio', color: 'indigo' },
      { id: 'outstanding', title: 'Outstanding A/R', value: fmtMoney(s.outstanding_receivables), change: 0, icon: 'efficiency', color: 'teal' },
    ];
  }, [data]);

  const comparisonLabels: Record<string, string | undefined> = useMemo(() => {
    if (!data?.comparison) return {};
    const c = data.comparison;
    return {
      'total-revenue': pctLabel(data.stats.total_revenue, c.previous_period_revenue),
      'gross-margin': c.previous_period_margin_pct !== undefined
        ? `${data.stats.gross_margin_pct - c.previous_period_margin_pct >= 0 ? '+' : ''}${(data.stats.gross_margin_pct - c.previous_period_margin_pct).toFixed(1)}pp vs last period`
        : undefined,
      'avg-order-value': undefined,
    };
  }, [data]);

  const revenueTrend: RevenueDataPoint[] = data?.revenue_trend.map((r) => ({ day: r.day, revenue: r.revenue })) ?? [];
  const topCustomers: TopCustomer[] = data?.top_customers ?? [];
  const bestSelling: BestSellingProduct[] = (data?.best_selling ?? []).map((p, i) => ({
    rank: p.rank,
    name: p.name,
    category: p.category,
    categoryColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    unitsSold: p.units_sold,
    performance: data && data.best_selling[0] ? Math.round((p.units_sold / Math.max(data.best_selling[0].units_sold, 1)) * 100) : 0,
  }));
  const orderStatus: OrderStatusData[] = (data?.order_status ?? []).map((o) => ({
    label: o.label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    value: o.value,
    color: STATUS_COLOR[o.label] ?? '#6B7280',
  }));
  const routePerformance: RoutePerformance[] = data?.route_performance.map((r) => ({ route: r.route, revenue: r.revenue })) ?? [];
  const needsAttention = data?.needs_attention ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <label className={styles.compareToggle}>
          <input
            type="checkbox"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
          />
          Compare vs previous period
        </label>
      </div>

      <AsyncBoundary
        loading={loading && !data}
        error={error}
        empty={false}
        onRetry={() => load(compare)}
      >
        <div className={styles.statsGrid}>
          {stats.slice(0, 4).map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              change={stat.change}
              icon={stat.icon}
              color={stat.color}
              onClick={DRILLDOWN_CARDS.has(stat.id) ? () => setDrilldownTitle(stat.title) : undefined}
              comparisonLabel={compare ? comparisonLabels[stat.id] : undefined}
            />
          ))}
        </div>

        <div className={styles.statsGrid}>
          {stats.slice(4, 8).map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              change={stat.change}
              icon={stat.icon}
              color={stat.color}
              onClick={DRILLDOWN_CARDS.has(stat.id) ? () => setDrilldownTitle(stat.title) : undefined}
              comparisonLabel={compare ? comparisonLabels[stat.id] : undefined}
            />
          ))}
        </div>

        <div className={styles.chartsRow}>
          <ChartCard title="Revenue Trend (7 Days)" icon={<TrendIcon />} className={styles.chartLarge}>
            <LineChart data={revenueTrend.map((d) => ({ label: d.day, value: d.revenue }))} height={220} color="#3B82F6" />
          </ChartCard>
          <ChartCard title="Top Customers by Revenue" icon={<CustomersIcon />} className={styles.chartMedium}>
            <BarChart data={topCustomers.map((d) => ({ label: d.name, value: d.revenue }))} height={220} color="#10B981" />
          </ChartCard>
        </div>

        <div className={styles.chartsRow}>
          <ChartCard title="Best-Selling Products" icon={<ProductIcon />} className={styles.chartLarge}>
            <ProductsTable products={bestSelling} />
          </ChartCard>
          <ChartCard title="Order Status" icon={<OrderIcon />} className={styles.chartMedium}>
            <DonutChart data={orderStatus} size={140} strokeWidth={20} />
          </ChartCard>
        </div>

        <div className={styles.chartsRow}>
          <ChartCard title="Route Performance by Revenue" icon={<RouteIcon />} className={styles.chartLarge}>
            <BarChart
              data={routePerformance.map((d) => ({ label: d.route, value: d.revenue }))}
              height={200}
              color="#10B981"
              orientation="horizontal"
            />
          </ChartCard>
          <ChartCard title="Needs Attention" icon={<AlertIcon />} className={styles.chartMedium}>
            <NeedsAttentionList items={needsAttention} />
          </ChartCard>
        </div>
      </AsyncBoundary>

      {drilldownTitle && (
        <DrilldownModal title={drilldownTitle} onClose={() => setDrilldownTitle(null)} />
      )}
    </div>
  );
};

export const AdminDashboardPage: React.FC = () => {
  return (
    <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
      <AdminDashboard />
    </ProtectedRoute>
  );
};

export default AdminDashboard;
